/**
 * ContentPage component renders the layout and content for a docs page.
 *
 * It handles:
 * - Fetching page data from GraphQL
 * - Constructing navigation, breadcrumbs etc.
 * - Rendering page layout
 * - Rendering markdown content
 * @author Milos Tanaskovic
 */
import React from "react";
import { PageProps, graphql } from "gatsby";
import Layout, { NetsProduct } from "components/layout";
import {
  getNavigation,
  getRightNavigationFromContent,
  TableOfContentJson,
  TableOfContentReference
} from "utils/navigation";
import { useGuideResourceSet } from "hooks/resource-set";
import Content from "./components/content";
import { Box } from "designsystem";
import { useDocsBreadcrumbs } from "hooks/breadcrumbs";
import { useSelector } from "react-redux";
import { AppState, PortalTheme } from "state/app";

export const query = graphql`
  query ContentPageTemplate($id: String, $productId: String) {
    contentfulContentPage(contentful_id: { eq: $id }) {
      id
      title
      content {
        ...ContentList
      }
      metaDescription
    }
    contentfulTableOfContent(product: { contentfulid: { eq: $productId } }) {
      ...TableOfContentNode
    }

    allContentfulLinkList(
      filter: {
        contentpage: { elemMatch: { contentful_id: { eq: $id } } }
        enableRegionPicker: { eq: true }
      }
    ) {
      edges {
        node {
          ...LinkListNode
        }
      }
    }
  }
`;

export interface ContentPageProps {
  id: string;
  locale: string;
  product: NetsProduct;
  productId: string;
  pagePath: string;
  isStartPage: boolean;
}

const ContentPage: React.FC<
  PageProps<GatsbyTypes.ContentPageTemplateQuery, ContentPageProps>
> = ({ pageContext, data }) => {
  const contentPage = data.contentfulContentPage;
  const title = contentPage?.title;
  const product = pageContext.product;
  const locale = pageContext.locale;
  const pagePath = pageContext.pagePath;
  const content = contentPage?.content;
  const localizedLinkLists = data.allContentfulLinkList.edges.map(
    (node) => node.node
  );
  const theme = useSelector<AppState, PortalTheme>(
    (state) => state.portalTheme
  );
  const metaDescription = contentPage?.metaDescription;

  const [overviewIntroTitle, overviewPageTitle] = useGuideResourceSet([
    "guide.overview-intro-title",
    "guide.overview-navigation-title"
  ]);

  const navigationReferences =
    data.contentfulTableOfContent?.tableOfContentReference;
  const navigationJson = data.contentfulTableOfContent?.tableOfContentJson;
  const navigation = getNavigation(
    product?.slug || "",
    locale,
    overviewPageTitle,
    navigationReferences as TableOfContentReference[],
    navigationJson as TableOfContentJson[]
  );

  const breadcrumbItems = useDocsBreadcrumbs(
    product,
    locale,
    pageContext.isStartPage
  );

  const lastBreadcrumb = [
    {
      title: title ?? "missing title",
      url: pagePath,
      active: true
    }
  ];
  const allBreadcrumbs = !pageContext.isStartPage
    ? [...breadcrumbItems, ...lastBreadcrumb]
    : [...breadcrumbItems];

  const rightNavigation = getRightNavigationFromContent(
    pagePath,
    content,
    overviewIntroTitle || "missing title"
  );

  const headerInContent = content?.[0]?.__typename === "ContentfulHeader";
  const header = !headerInContent ? (
    <Box marginLeft="gap" marginRight="gap" direction="column">
      <h1>{title}</h1>
    </Box>
  ) : undefined;

  return (
    <React.Fragment>
      {title && (
        <Layout
          locale={locale}
          theme={theme}
          pageTitle={title}
          product={product as NetsProduct}
          navigation={navigation}
          activeNavigationUrl={pagePath}
          onThisPage={rightNavigation}
          breadcrumbs={allBreadcrumbs}
          layout={headerInContent ? "with-hero" : undefined}
          description={metaDescription ?? undefined}
        >
          {header}
          {content && (
            <Content content={content} localizedContent={localizedLinkLists} />
          )}
        </Layout>
      )}
    </React.Fragment>
  );
};

export default ContentPage;
